import { TypedDocumentNode, gql } from '@apollo/client';
import { faUser } from '@fortawesome/pro-solid-svg-icons';
import { CSSProperties } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { StarBall } from '@sorare/core/src/atoms/icons/StarBall';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { LiveDot } from '@sorare/core/src/components/live/LiveDot';
import { ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_MATCH_CENTER } from '@sorare/core/src/constants/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { MatchCenterBanner_so5Fixture } from './__generated__/index.graphql';

const Root = styled(ButtonBase)`
  width: 100%;
  max-width: 345px;
  height: 200px;
  margin: auto;
  position: relative;
  display: flex;
  justify-content: center;

  background-image: var(--mobile-bg);
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;

  @media ${tabletAndAbove} {
    background-image: var(--desktop-bg);
    max-width: 1845px;
    border-radius: unset;
    border: unset;
    align-items: end;
    padding-bottom: var(--unit);
  }
`;
const StyledVertical = styled(Vertical).attrs({ center: true })`
  gap: var(--double-unit);
`;
const Title = styled(Horizontal).attrs({ as: 'p', gap: 0.5 })`
  font-weight: 300;
  font-size: 40px;
  @media ${tabletAndAbove} {
    font-size: 64px;
  }
`;
const Subtitle = styled(Horizontal).attrs({ as: 'p', gap: 0 })`
  font-weight: 300;
  font-size: 28px;
  @media ${tabletAndAbove} {
    font-size: 34px;
  }
`;
const IconWrapper = styled.div`
  display: flex;
  align-self: start;
  margin-top: var(--unit);
`;

type Props = {
  so5Fixture: MatchCenterBanner_so5Fixture;
  backgroundImage: { desktop: string; mobile: string };
};

export const MatchCenterBanner = ({ so5Fixture, backgroundImage }: Props) => {
  const { generateSportContextPath } = useSportContext();
  const hasLiveGames = so5Fixture.myLivePlayerGameScoresCount > 0;

  return (
    <Root
      style={
        {
          '--desktop-bg': `url(${backgroundImage.desktop})`,
          '--mobile-bg': `url(${backgroundImage.mobile})`,
        } as CSSProperties
      }
      to={generateSportContextPath(
        ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_MATCH_CENTER,
        {
          params: {
            eventType: so5Fixture.type.toLowerCase(),
            fixture: so5Fixture.slug,
          },
        }
      )}
    >
      <StyledVertical>
        <Title>
          <FormattedMessage
            {...(so5Fixture.sport === Sport.FOOTBALL
              ? fantasy.matchCenter
              : fantasy.gameCenter)}
          />
          <IconWrapper>
            <StarBall width={20} height={20} />
          </IconWrapper>
        </Title>
        <Vertical>
          <Subtitle>
            <FormattedMessage
              id="MatchCenterBanner.goLive"
              defaultMessage="Go Live"
            />
            {hasLiveGames && (
              <IconWrapper>
                <LiveDot color="var(--c-red-600)" animate />
              </IconWrapper>
            )}
          </Subtitle>

          <LabelS color={hasLiveGames ? 'var(--c-white)' : 'var(--c-nd-600)'}>
            <Horizontal center gap={0.5} as="span">
              <FontAwesomeIcon icon={faUser} width={12} height={12} />
              <FormattedNumber value={so5Fixture.myLivePlayerGameScoresCount} />
            </Horizontal>
          </LabelS>
        </Vertical>
      </StyledVertical>
    </Root>
  );
};

MatchCenterBanner.fragments = {
  so5Fixture: gql`
    fragment MatchCenterBanner_so5Fixture on So5Fixture {
      slug
      type
      sport
      myLivePlayerGameScoresCount
    }
  ` as TypedDocumentNode<MatchCenterBanner_so5Fixture>,
};
